import { Collection, Pagination } from "@shared/types";

export const roundingValue = (
  value: number,
  rounding: number,
  options?: { minValue: number },
): number => {
  const roundedValue = rounding * Math.round(value / rounding);

  if (options?.minValue && options.minValue > roundedValue) {
    return options.minValue;
  }

  return roundedValue;
};

export function extractError(error: any): string {
  let message = "";
  if (error && error.message) {
    message += error.message;
  }

  if (error && error.fails) {
    message += "\n" + JSON.stringify(error.fails);
  }

  return message;
}

export function getInitial(text: string): string {
  if (text) {
    const data = text.split(" ");

    if (data && data.length >= 2) {
      return `${data[0].charAt(0).toUpperCase()}${data[1].charAt(0).toUpperCase()}`;
    }

    return `${data[0].charAt(0).toUpperCase()}${data[0].charAt(1).toUpperCase()}`;
  }

  return "";
}

export function getSlug(id: string, text: string): string {
  if (id && text) {
    const slug = text.replace(/\//g, '').replace(/\(/g, '').replace(/\)/g, '').replace(/ /g, '-').toLowerCase();

    return slug + '-' + id;
  }

  return id;
}

export function getIdFromSlug(slug: string): string | null {
  if (!slug) return null;

  const lastDashIndex = slug.lastIndexOf("-");
  if (lastDashIndex === -1) return null;

  return slug.substring(lastDashIndex + 1);
}

export const imageFallback =
  (fallback?: string) =>
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;

      img.onerror = null;

      img.removeAttribute("srcset");

      img.src = fallback || "/blank-white.webp";
    };

export function mapCollectionToPagination(collection: Collection<any>): Pagination {
  return {
    perPage: collection?.meta?.perPage || collection?.meta?.pagination?.perPage || 10,
    currentPage: collection?.meta?.currentPage || collection?.meta?.pagination?.currentPage || 1,
    from: collection?.meta?.from || collection?.meta?.pagination?.from || 1,
    to: collection?.meta?.to || collection?.meta?.pagination?.to || 1,
    lastPage: collection?.meta?.lastPage || collection?.meta?.pagination?.lastPage || 1,
    total: collection?.meta?.total || collection?.meta?.pagination?.total || 1,
  }
}

export function parseFilter(key: string, value: any, initialValue?: any, toggle?: boolean) {
  let params = initialValue || {};

  if (typeof toggle === "boolean") {
    if (toggle) {
      if (!params[key]) {
        params = {
          ...params,
          [key]: [value],
        };
      } else if (!!params[key] && Array.isArray(params[key])) {
        params[key].push(value);
      } else if (!!params[key]) {
        params[key] = value;
      } else {
        params[key] = null;
      }
    } else {
      if (!!params[key] && Array.isArray(params[key])) {
        const idx = params[key].findIndex((it: any) => it?.id === value.id);
        params[key].splice(idx, 1);

        if (params[key]?.length <= 0) {
          delete params[key];
        }
      } else if (!!params[key]) {
        delete params[key];
      }
    }
  } else {
    if (!params[key]) {
      params = {
        ...params,
        [key]: [value],
      };
    } else if (!!params[key]) {
      params[key] = value;
    } else {
      params[key] = null;
    }
  }

  params["page"] = undefined;

  return params;
}

export function isFilterValueExist(params: any, key: string, value?: string | number): boolean {
  if (params) {
    if (params[key] && Array.isArray(params[key])) {
      if (value) {
        return params[key].findIndex(it => it.id === value) !== -1;
      }

      return !!params[key];
    }

    if (value) {
      return params[key]?.id === value;
    }

    return !!params[key];
  }

  return false;
}

export function getFilterValue(params: any, key: string, value?: string | number): any {
  if (params) {
    if (params[key] && Array.isArray(params[key])) {
      if (value) {
        return params[key].find(it => it.id === value);
      }

      return params[key];
    }

    if (value) {
      return params[key]?.id;
    }

    return params[key];
  }

  return null;
}

type ValueDescriptor = Record<string, string[]>;
export function mapFilterValues(params: any, valueDescriptors?: ValueDescriptor): any {
  if (!params) return params;

  const result: Record<string, any> = {};

  for (const key in params) {
    const value = params[key];
    const descriptor = valueDescriptors?.[key];

    const mapItem = (item: any) => {
      if (!item) return item;

      if (descriptor && descriptor.length > 0) {
        if (descriptor.length === 1) {
          return item?.[descriptor[0]];
        }

        const mapped: Record<string, any> = {};
        descriptor.forEach(field => {
          mapped[field] = item?.[field];
        });

        return mapped;
      }

      return item?.id;
    };

    if (Array.isArray(value)) {
      result[key] = value.map(mapItem);
    } else {
      result[key] = mapItem(value);
    }
  }

  return result;
}

export function getAge(date?: Date | string): number {
  if (date) {
    const bod = date instanceof Date ? date : new Date(date);
    const diff = Date.now() - bod.getTime();
    const ageDate = new Date(diff);

    return Math.abs(ageDate.getUTCFullYear() - 1970);
  }

  return 0;
}